import { Injectable } from '@angular/core';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import * as Cesium from 'cesium';
import { CoresService } from './cores.service';
import { AreaMapEntity } from './area-map';
import { BuildingMapEntity } from './building-map-entity-test';
import { FloorService } from './floor.service';
import { Subject, Subscription, interval } from 'rxjs';
import { filter, withLatestFrom } from 'rxjs/operators';
import { NgZone } from '@angular/core';
import { BuildingStateService } from './building-state.service';
import { DeviceService } from './device.service';
import { BlenderBuildingService, ThreeBuildingHost } from './blender-building.service';

export interface BuildingData {
  id: string;
  name?: string;
  address?: string;
  longitude: number;
  latitude: number;
  height?: number;
  width?: number;
  length?: number;
  scale?: number;
  color?: number;
  modelUrl?: string;
  floors?: FloorData[];
  totalRooms?: number;
  areaMapEntity?: any; // Reference to the original AreaMapEntity
  perimeters?: any[]; // Associated perimeter data
}

export interface AreaMapBuildingData extends BuildingData {
  areas: AreaMapEntity[];
  floorPlans?: any[];
  rotationCoords?: any;
}

export interface FloorData {
  id: string;
  name?: string;
  level: number;
  height?: number;
  rooms?: RoomData[];
  coordinates?: number[][];
}

export interface RoomData {
  id: string;
  x: number;
  z: number;
  width: number;
  length: number;
  height?: number;
  color?: number;
  name?: string;
  coordinates?: number[][];
}

@Injectable({
  providedIn: 'root'
})
export class ThreeIntegrationService {
  static readonly FLOOR_HIGHLIGHT_ENABLED = BlenderBuildingService.FLOOR_HIGHLIGHT_ENABLED;

  private threeScene: THREE.Scene | null = null;
  private threeRenderer: THREE.WebGLRenderer | null = null;
  private threeCamera: THREE.PerspectiveCamera | null = null;
  private buildingModels: Map<string, THREE.Group> = new Map();
  private cesiumThreeContainer: HTMLElement | null = null;
  private isThreeJSActive: boolean = false;
  private renderLoopCallback: ((scene: Cesium.Scene, time: Cesium.JulianDate) => void) | null = null;
  private raycaster: THREE.Raycaster = new THREE.Raycaster();
  private mouse: THREE.Vector2 = new THREE.Vector2();
  private controls: OrbitControls | null = null;
  private hoveredObject: THREE.Object3D | null = null;
  private tooltipDiv: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;

  private readonly PERIMETER_SAFETY_STATUS_SAFE = 'safe';
  private readonly PERIMETER_SAFETY_STATUS_UNSAFE = 'unsafe';
  private readonly PERIMETER_SAFETY_STATUS_UNKNOWN = 'unknown';
  private readonly PERIMETER_USER_COUNT_STATUS_NO_USERS = 'no_users';
  private readonly PERIMETER_USER_COUNT_STATUS_SOME_USERS = 'some_users';

  // Area map integration properties
  private currentAreaMapBuilding: AreaMapBuildingData | null = null;
  private areaMapEntities: Map<string, any> = new Map();
  public static statusIndicators: Map<string, THREE.Sprite> = new Map();

  // Properties for managing the floor preview to prevent WebGL context leaks
  private previewRenderer: THREE.WebGLRenderer | null = null;
  private previewControls: OrbitControls | null = null;
  private previewAnimationId: number | null = null;

  private buildingContainerGroup: THREE.Group | null = null;
  private currentBuildingPosition: { longitude: number; latitude: number; height: number } | null = null;
  private buildingGeographicCenter: { lat: number; lon: number } | null = null;

  private currentBuildingEntity: any = null;
  private previouslyHighlightedFloor: string | null = null;
  private floorClickSubject = new Subject<{ floorId: string; floorNumberHint?: number }>();
  public floorClick$ = this.floorClickSubject.asObservable();
  public static roomMeshMap = new Map<string, THREE.Mesh>();
  private buildingEntities: Map<string, any> = new Map();
  private buildingMarkerClickSubject = new Subject<{ data: any, x: number, y: number }>();
  public buildingMarkerClick$ = this.buildingMarkerClickSubject.asObservable();

  private selectedObject: THREE.Object3D | null = null;
  private campusUserCountSubscription: Subscription | null = null;
  beacons: any[] = [];
  userByPerimeter: any;
  constructor(
    private coresService: CoresService,
    private floorService: FloorService,
    private zone: NgZone,
    private buildingStateService: BuildingStateService,
    private deviceService: DeviceService,
    private blenderBuilding: BlenderBuildingService
  ) {
    this.blenderBuilding.setHost(this.buildBlenderHost());
  }


  /**
   * Initialize Three.js integration with Cesium
   */
  // ------------------------Building settings --------------------------
  initializeThreeJSIntegration(container: HTMLElement): void {
    this.cesiumThreeContainer = container;
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.resizeThreeJSRenderer();
      });
      this.resizeObserver.observe(this.cesiumThreeContainer);
    }

    this.setupThreeJSScene();
    this.setupThreeJSRenderer();
    this.setupThreeJSCamera();
    this.setupControls();
    this.setupEventListeners();

    // Append renderer to container immediately for overlay
    if (this.threeRenderer && this.cesiumThreeContainer) {
      this.cesiumThreeContainer.appendChild(this.threeRenderer.domElement);
    }

    this.startRenderLoop();

  }
  /**
    * Start the render loop
    */
  private startRenderLoop(): void {
    const viewer = this.coresService.getViewer();
    if (!viewer) return;

    if (!this.renderLoopCallback) {
      this.renderLoopCallback = () => {
        const shouldRender = this.isThreeJSActive || this.blenderBuilding.isGlbMapOverlayActive();
        if (shouldRender && this.threeRenderer && this.threeScene && this.threeCamera) {
          this.resizeThreeJSRenderer();
          this.synchronizeCameras();
          this.threeRenderer.render(this.threeScene, this.threeCamera);
        }
      };
    }

    viewer.scene.postRender.removeEventListener(this.renderLoopCallback);
    viewer.scene.postRender.addEventListener(this.renderLoopCallback);
  }
  private lightsGroup: THREE.Group = new THREE.Group();

  /**
   * Set up Three.js scene with enhanced lighting for building visualization
   */
  private setupThreeJSScene(): void {
    this.threeScene = new THREE.Scene();
    // Start with transparent background - will be set when building is activated
    this.threeScene.background = null;

    this.setLightingMode();
  }

  /**
   * Dynamically switch scene lighting between Normal Three.js 3D view (BEFORE) and Blender view (AFTER)
   */
  public setLightingMode(isBlenderView?: boolean): void {
    if (!this.threeScene) return;

    const isBlender = isBlenderView !== undefined
      ? isBlenderView
      : (this.blenderBuilding?.isGlbMapOverlayActive() ?? false);

    if (isBlender) {
      // Blender View lighting setup via Blender service
      this.blenderBuilding?.setupBlenderLighting(this.threeScene, this.lightsGroup);
    } else {
      // Normal Three.js View lighting (Original setup)
      if (!this.threeScene.children.includes(this.lightsGroup)) {
        this.threeScene.add(this.lightsGroup);
      }
      this.lightsGroup.clear();

      const ambientLight = new THREE.AmbientLight(0xffffff, 2.2);
      this.lightsGroup.add(ambientLight);

      // Main directional light (sun) - positioned for good building shadows
      const sunLight = new THREE.DirectionalLight(0xffffff, 2.5);
      sunLight.position.set(100, 200, 100);
      sunLight.castShadow = true;
      sunLight.shadow.mapSize.width = 4096;
      sunLight.shadow.mapSize.height = 4096;
      sunLight.shadow.camera.near = 0.5;
      sunLight.shadow.camera.far = 2000;
      sunLight.shadow.camera.left = -500;
      sunLight.shadow.camera.right = 500;
      sunLight.shadow.camera.top = 500;
      sunLight.shadow.camera.bottom = -500;
      this.lightsGroup.add(sunLight);

      // Fill light from opposite direction
      const fillLight = new THREE.DirectionalLight(0xffffff, 1.2);
      fillLight.position.set(-80, 150, -80);
      this.lightsGroup.add(fillLight);

      // Hemisphere light for natural outdoor lighting
      const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0xd0d0d0, 1.2);
      this.lightsGroup.add(hemisphereLight);

      // Accent lights for building details
      const accentLight1 = new THREE.SpotLight(0xffffff, 1.0, 500, Math.PI / 6, 0.5, 2);
      accentLight1.position.set(150, 150, 150);
      accentLight1.castShadow = true;
      this.lightsGroup.add(accentLight1);

      const accentLight2 = new THREE.SpotLight(0xffffff, 1.0, 500, Math.PI / 6, 0.5, 2);
      accentLight2.position.set(-150, 150, -150);
      accentLight2.castShadow = true;
      this.lightsGroup.add(accentLight2);
    }
  }

  /**
   * Set up Three.js WebGL renderer as transparent overlay
   */
  private setupThreeJSRenderer(): void {
    if (!this.cesiumThreeContainer) return;

    if (this.threeRenderer) {
      this.threeRenderer.dispose();
      if (this.threeRenderer.domElement && this.threeRenderer.domElement.parentNode) {
        this.threeRenderer.domElement.parentNode.removeChild(this.threeRenderer.domElement);
      }
    }
    if (!this.threeRenderer) {
      this.threeRenderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true,
        logarithmicDepthBuffer: true,
        preserveDrawingBuffer: true
      });
    }

    // Make renderer transparent to show Cesium beneath
    this.threeRenderer.setClearColor(0x000000, 0);
    this.threeRenderer.setSize(
      this.cesiumThreeContainer.clientWidth,
      this.cesiumThreeContainer.clientHeight
    );

    // Position as overlay
    this.threeRenderer.domElement.style.position = 'absolute';
    this.threeRenderer.domElement.style.top = '0';
    this.threeRenderer.domElement.style.left = '0';
    this.threeRenderer.domElement.style.pointerEvents = 'none';
    this.threeRenderer.domElement.style.zIndex = '10';

    // Enable shadows for realistic rendering
    this.threeRenderer.shadowMap.enabled = true;
    this.threeRenderer.shadowMap.type = THREE.PCFShadowMap;

    this.cesiumThreeContainer.appendChild(this.threeRenderer.domElement);
  }

  /**
   * Set up Three.js perspective camera with optimal building view settings
   */
  private setupThreeJSCamera(): void {
    if (!this.cesiumThreeContainer) return;

    const width = this.cesiumThreeContainer.clientWidth;
    const height = this.cesiumThreeContainer.clientHeight;

    this.threeCamera = new THREE.PerspectiveCamera(60, width / height, 0.1, 50000);

    // Position camera for optimal building viewing
    this.threeCamera.position.set(150, 120, 150);
    this.threeCamera.lookAt(0, 50, 0);

    // Set camera up vector
    this.threeCamera.up.set(0, 1, 0);
  }

  /**
   * Set up orbit controls for Three.js camera with building-optimized settings
   */
  private setupControls(): void {
    if (!this.threeCamera || !this.threeRenderer) return;

    if (this.controls) {
      this.controls.dispose();
    }

    this.controls = new OrbitControls(this.threeCamera, this.threeRenderer.domElement);

    // Enhanced controls for building visualization
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.screenSpacePanning = true;
    this.controls.enablePan = true;
    this.controls.enableZoom = true;
    this.controls.enableRotate = true;

    // Set target to building center
    this.controls.target.set(0, 50, 0);

    // Constrain camera movement for building viewing
    // Increased ranges to accommodate properly-scaled building data
    this.controls.minDistance = 5;
    this.controls.maxDistance = 5000;
    this.controls.minPolarAngle = 0;
    this.controls.maxPolarAngle = Math.PI * 0.8; // Prevent going below ground

    this.controls.rotateSpeed = 0.5;
    this.controls.panSpeed = 0.8;
    this.controls.zoomSpeed = 1.2;

    this.controls.autoRotate = false;
    this.controls.autoRotateSpeed = 1.0;

    this.animate();
  }

  private animate(): void {
    requestAnimationFrame(() => this.animate());

    if (!this.threeScene || !this.threeCamera || !this.threeRenderer) return;

    if (this.controls) {
      this.controls.update();
    }

    if (this.selectedObject) {
      this.updatePopupPosition();
    }

    this.threeRenderer.render(this.threeScene, this.threeCamera);
  }

  private updatePopupPosition(): void {
    if (!this.selectedObject || !this.threeCamera || !this.threeRenderer) return;

    const worldPos = new THREE.Vector3();
    this.selectedObject.getWorldPosition(worldPos);

    const vector = worldPos.clone().project(this.threeCamera);

    const width = this.threeRenderer.domElement.clientWidth;
    const height = this.threeRenderer.domElement.clientHeight;

    const x = (vector.x * 0.5 + 0.5) * width;
    const y = (-vector.y * 0.5 + 0.5) * height;

    this.zone.run(() => {
      this.buildingStateService.setPopupPosition({ x, y });
    });
  }
  /**
   * Set up event listeners for interaction
   */
  private setupEventListeners(): void {
    if (!this.threeRenderer) return;

    this.threeRenderer.domElement.addEventListener('click', this.onMouseClick.bind(this));
    this.threeRenderer.domElement.addEventListener('mousemove', this.onMouseMove.bind(this));
    window.addEventListener('resize', this.onWindowResize.bind(this));

    this.tooltipDiv = document.createElement('div');
    this.tooltipDiv.style.position = 'absolute';
    this.tooltipDiv.style.padding = '8px 12px';
    this.tooltipDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    this.tooltipDiv.style.color = '#fff';
    this.tooltipDiv.style.borderRadius = '4px';
    this.tooltipDiv.style.fontSize = '12px';
    this.tooltipDiv.style.pointerEvents = 'none';
    this.tooltipDiv.style.zIndex = '100';
    this.tooltipDiv.style.display = 'none';
    this.tooltipDiv.style.whiteSpace = 'nowrap';
    document.body.appendChild(this.tooltipDiv);
    const container = document.getElementById('cesiumContainer');
    if (container) {
      container.addEventListener('click', this.onMouseClick.bind(this));
      container.addEventListener('mousemove', this.onMouseMove.bind(this));
    }

    // Disable Cesium's double-click zoom
    const viewer = this.coresService.getViewer();
    if (viewer && viewer.screenSpaceEventHandler) {
      viewer.screenSpaceEventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
    }
  }
  /**
    * Handle mouse click events for object interaction
    */
  private onMouseClick(event: MouseEvent): void {
    if (!this.threeCamera || !this.threeScene) return;

    if (this.blenderBuilding.handleGlbMapClick(event)) {
      return;
    }

    const rect = (event.target as HTMLElement).getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return;
    }
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.threeCamera);
    const intersects = this.raycaster.intersectObjects(this.threeScene.children, true);

    if (intersects.length > 0) {
      this.onObjectClick(intersects[0].object);
    }
  }

  /**
   * Handle mouse move events
   */
  private onMouseMove(event: MouseEvent): void {
    if (!this.threeCamera || !this.threeScene) return;

    if (this.blenderBuilding.isGlbMapOverlayActive()) {
      if (this.hoveredObject) {
        this.hideTooltip();
        this.hoveredObject = null;
      }
      return;
    }

    const rect = (event.target as HTMLElement).getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    if (this.isThreeJSActive) {
      this.raycaster.setFromCamera(this.mouse, this.threeCamera);
      const intersects = this.raycaster.intersectObjects(this.threeScene.children, true);
      let hoveredMesh: THREE.Object3D | null = null;
      if (intersects.length > 0) {
        let obj = intersects[0].object;
        while (obj.parent && !(obj.userData['roomId'] || obj.userData['floorId'])) {
          obj = obj.parent;
        }
        hoveredMesh = obj;
      }

      if (hoveredMesh && (hoveredMesh.userData['roomId'] || hoveredMesh.userData['floorId'])) {
        if (this.hoveredObject !== hoveredMesh) {
          this.hoveredObject = hoveredMesh;
          const label = hoveredMesh.userData['label'] || hoveredMesh.userData['floorId'];
          this.showTooltip(label, event.clientX, event.clientY);
        } else {
          this.updateTooltipPosition(event.clientX, event.clientY);
        }
      } else {
        if (this.hoveredObject) {
          this.hideTooltip();
          this.hoveredObject = null;
        }
      }
    }
  }
  /**
 * Show tooltip with label
 */
  private showTooltip(label: string, x: number, y: number): void {
    if (!this.tooltipDiv) return;
    this.tooltipDiv.textContent = label;
    this.tooltipDiv.style.display = 'block';
    this.tooltipDiv.style.left = (x + 10) + 'px';
    this.tooltipDiv.style.top = (y + 10) + 'px';
  }

  /**
   * Update tooltip position
   */
  private updateTooltipPosition(x: number, y: number): void {
    if (!this.tooltipDiv) return;
    this.tooltipDiv.style.left = (x + 10) + 'px';
    this.tooltipDiv.style.top = (y + 10) + 'px';
  }

  /**
   * Hide tooltip
   */
  private hideTooltip(): void {
    if (!this.tooltipDiv) return;
    this.tooltipDiv.style.display = 'none';
  }

  /**
   * Handle window resize events
   */
  private onWindowResize(): void {
    this.resizeThreeJSRenderer();
  }
  private onObjectClick(object: THREE.Object3D): void {
    // Find the building, floor, or room associated with this object
    let targetObject = object;

    // Traverse up the hierarchy to find floor, room, or building
    while (targetObject.parent &&
      !targetObject.userData['buildingId'] &&
      !targetObject.userData['roomId'] &&
      !targetObject.userData['floorId'] &&
      targetObject.userData['type'] !== 'floor') {
      targetObject = targetObject.parent;
    }

    // Check if clicked object is a floor (by userData type or floorId)
    const isFloor = targetObject.userData['type'] === 'floor' ||
      targetObject.userData['floorId'] !== undefined ||
      (targetObject.userData['label'] && this.areaMapEntities.has(targetObject.userData['label']));

    if (isFloor || targetObject.userData['floorId']) {
      // Get floor ID - try multiple possible identifiers coming from the 3D mesh
      const clickedFloorId =
        targetObject.userData['floorId'] ||
        targetObject.userData['label'] ||
        targetObject.userData['id'];

      if (clickedFloorId) {
        this.highlightFloorById(clickedFloorId, true);

        this.floorClickSubject.next({ floorId: clickedFloorId });

        if (this.cesiumThreeContainer) {
          this.focusFloorOnArea(clickedFloorId, this.cesiumThreeContainer);
        }

        const originalFloors = this.currentBuildingEntity?.getFloorPerimeters?.() ?? this.currentBuildingEntity?.floorPerimeters ?? [];

        const floor: any = {
          perimeterId: clickedFloorId,
          id: clickedFloorId,
          label: targetObject.userData['label']
        };

        // Find the exact floor entity - 
        let originalFloor = floor;
        if (floor && (floor.perimeterId || floor.id)) {
          const found = originalFloors.find((f: any) =>
            f && (f.perimeterId === floor.perimeterId || f.id === floor.id)
          );
          if (found) originalFloor = found;
        }

        if (originalFloor === floor) {
          const areaMapFloor = this.getAreaMapEntity(clickedFloorId);
          if (areaMapFloor) {
            originalFloor = areaMapFloor;
          }
        }

        // Ensure we have a valid entity instance
        if (originalFloor === floor || !originalFloor) {
          return; // Can't proceed without a valid floor entity
        }

        let locations: number[][] = [];
        if (originalFloor.geometry && originalFloor.geometry.coordinates) {
          const coords = originalFloor.geometry.coordinates[0]; // First ring of polygon
          if (Array.isArray(coords) && coords.length > 0) {
            locations = coords.map((coord: number[]) => {
              if (Array.isArray(coord) && coord.length >= 2) {
                return [coord[1], coord[0]]; // Convert [lon, lat] to [lat, lon]
              }
              return null;
            }).filter((loc: any) => loc !== null) as number[][];
          }
        }
        if ((!locations || locations.length === 0) && originalFloor.locations && Array.isArray(originalFloor.locations)) {
          locations = originalFloor.locations;
        }
        let centerpoint = originalFloor.centerpoint;
        if (!centerpoint || !centerpoint.latitude) {
          if (locations && locations.length > 0) {
            const lats = locations.map(loc => loc[0]);
            const lons = locations.map(loc => loc[1]);
            centerpoint = {
              latitude: lats.reduce((a, b) => a + b, 0) / lats.length,
              longitude: lons.reduce((a, b) => a + b, 0) / lons.length
            };
          } else if (this.currentBuildingEntity) {
            centerpoint = {
              latitude: this.currentBuildingEntity.initViewPosLat || 0,
              longitude: this.currentBuildingEntity.initViewPosLon || 0
            };
          }
        }

        let perimeters: any = originalFloor.perimeters;
        if (!perimeters || Array.isArray(perimeters)) {
          const floorPerimeterId = originalFloor.perimeterId || originalFloor.id;
          if (Array.isArray(this.currentBuildingEntity.roomPerimeters)) {
            const roomsForFloor = this.currentBuildingEntity.roomPerimeters.filter(
              (r: any) => r && Array.isArray(r.ancestors) && r.ancestors.includes(floorPerimeterId)
            );
            const map: any = {};
            roomsForFloor.forEach((r: any) => {
              if (r && r.id != null) {
                let roomLocations: number[][] = [];
                if (typeof r.getCoordinates === 'function') {
                  const coords = r.getCoordinates();
                  if (Array.isArray(coords) && coords.length > 0) {
                    roomLocations = coords;
                  }
                } else if (r.locations && Array.isArray(r.locations)) {
                  roomLocations = r.locations;
                } else if (r.geometry && r.geometry.coordinates) {
                  const coords = r.geometry.coordinates[0];
                  if (Array.isArray(coords) && coords.length > 0) {
                    roomLocations = coords.map((coord: number[]) => {
                      if (Array.isArray(coord) && coord.length >= 2) {
                        return [coord[1], coord[0]]; // Convert [lon, lat] to [lat, lon]
                      }
                      return null;
                    }).filter((loc: any) => loc !== null) as number[][];
                  }
                }
                // Try to get from areaMapEntity
                if (roomLocations.length === 0 && r.id) {
                  const areaMapRoom = this.getAreaMapEntity(r.id);
                  if (areaMapRoom) {
                    if (typeof areaMapRoom.getCoordinates === 'function') {
                      const coords = areaMapRoom.getCoordinates();
                      if (Array.isArray(coords) && coords.length > 0) {
                        roomLocations = coords;
                      }
                    } else if (areaMapRoom.locations && Array.isArray(areaMapRoom.locations)) {
                      roomLocations = areaMapRoom.locations;
                    }
                  }
                }

                if (roomLocations.length > 0) {
                  r.locations = roomLocations;
                }

                if (!r.name && r.label) {
                  r.name = r.label;
                }
                if (!r.name && r.getName && typeof r.getName === 'function') {
                  r.name = r.getName();
                }
                if (!r.arcAngelId && r.id) {
                  r.arcAngelId = r.id;
                }
                map[String(r.id)] = r;
              }
            });
            perimeters = map;
          } else {
            perimeters = {};
          }
        } else {
          Object.values(perimeters).forEach((p: any) => {
            if (p && (!p.locations || p.locations.length === 0)) {
              if (typeof p.getCoordinates === 'function') {
                const coords = p.getCoordinates();
                if (Array.isArray(coords) && coords.length > 0) {
                  p.locations = coords;
                }
              } else if (p.geometry && p.geometry.coordinates) {
                const coords = p.geometry.coordinates[0];
                if (Array.isArray(coords) && coords.length > 0) {
                  p.locations = coords.map((coord: number[]) => {
                    if (Array.isArray(coord) && coord.length >= 2) {
                      return [coord[1], coord[0]];
                    }
                    return null;
                  }).filter((loc: any) => loc !== null) as number[][];
                }
              }
              if ((!p.locations || p.locations.length === 0) && p.id) {
                const areaMapRoom = this.getAreaMapEntity(p.id);
                if (areaMapRoom) {
                  if (typeof areaMapRoom.getCoordinates === 'function') {
                    const coords = areaMapRoom.getCoordinates();
                    if (Array.isArray(coords) && coords.length > 0) {
                      p.locations = coords;
                    }
                  } else if (areaMapRoom.locations && Array.isArray(areaMapRoom.locations)) {
                    p.locations = areaMapRoom.locations;
                  }
                }
              }
            }
            if (p && !p.name) {
              if (p.label) {
                p.name = p.label;
              } else if (p.getName && typeof p.getName === 'function') {
                p.name = p.getName();
              }
            }
            if (p && !p.arcAngelId && p.id) {
              p.arcAngelId = p.id;
            }
          });
        }

        // Ensure perimeterId is set 
        if (!originalFloor.perimeterId && originalFloor.id) {
          originalFloor.perimeterId = originalFloor.id;
        }

        if (!originalFloor.locations || originalFloor.locations.length === 0) {
          originalFloor.locations = locations || [];
        }
        if (!originalFloor.centerpoint || !originalFloor.centerpoint.latitude) {
          originalFloor.centerpoint = centerpoint;
        }
        if (!originalFloor.perimeters) {
          originalFloor.perimeters = perimeters || {};
        }
        if (!originalFloor.building && this.currentBuildingEntity) {
          originalFloor.building = this.currentBuildingEntity;
        }

        if (originalFloor.perimeters && typeof originalFloor.perimeters === 'object') {
          Object.values(originalFloor.perimeters).forEach((perimeter: any) => {
            if (perimeter && (!perimeter.locations || perimeter.locations.length === 0)) {
              // Try to get locations from getCoordinates() method
              if (typeof perimeter.getCoordinates === 'function') {
                const coords = perimeter.getCoordinates();
                if (Array.isArray(coords) && coords.length > 0) {
                  perimeter.locations = coords;
                }
              }
              // Try to get locations from geometry.coordinates
              if ((!perimeter.locations || perimeter.locations.length === 0) &&
                perimeter.geometry && perimeter.geometry.coordinates) {
                const coords = perimeter.geometry.coordinates[0];
                if (Array.isArray(coords) && coords.length > 0) {
                  perimeter.locations = coords.map((coord: number[]) => {
                    if (Array.isArray(coord) && coord.length >= 2) {
                      return [coord[1], coord[0]]; // Convert [lon, lat] to [lat, lon]
                    }
                    return null;
                  }).filter((loc: any) => loc !== null) as number[][];
                }
              }
              // Try to get from areaMapEntity
              if ((!perimeter.locations || perimeter.locations.length === 0) && perimeter.id) {
                const areaMapRoom = this.getAreaMapEntity(perimeter.id);
                if (areaMapRoom) {
                  if (typeof areaMapRoom.getCoordinates === 'function') {
                    const coords = areaMapRoom.getCoordinates();
                    if (Array.isArray(coords) && coords.length > 0) {
                      perimeter.locations = coords;
                    }
                  } else if (areaMapRoom.locations && Array.isArray(areaMapRoom.locations)) {
                    perimeter.locations = areaMapRoom.locations;
                  }
                }
              }
            }
            // Ensure perimeter has name for display
            if (perimeter && !perimeter.name) {
              if (perimeter.label) {
                perimeter.name = perimeter.label;
              } else if (perimeter.getName && typeof perimeter.getName === 'function') {
                perimeter.name = perimeter.getName();
              }
            }
            // Ensure perimeter has arcAngelId for user API
            if (perimeter && !perimeter.arcAngelId && perimeter.id) {
              perimeter.arcAngelId = perimeter.id;
            }
          });
        } else if (!originalFloor.perimeters || Object.keys(originalFloor.perimeters || {}).length === 0) {
          // If perimeters don't exist, try to build them from roomPerimeters
          const floorPerimeterId = originalFloor.perimeterId || originalFloor.id;
          if (Array.isArray(this.currentBuildingEntity.roomPerimeters)) {
            const roomsForFloor = this.currentBuildingEntity.roomPerimeters.filter(
              (r: any) => r && Array.isArray(r.ancestors) && r.ancestors.includes(floorPerimeterId)
            );
            const map: any = {};
            roomsForFloor.forEach((r: any) => {
              if (r && r.id != null) {
                // Extract locations for the room
                let roomLocations: number[][] = [];
                if (typeof r.getCoordinates === 'function') {
                  const coords = r.getCoordinates();
                  if (Array.isArray(coords) && coords.length > 0) {
                    roomLocations = coords;
                  }
                } else if (r.locations && Array.isArray(r.locations)) {
                  roomLocations = r.locations;
                } else if (r.geometry && r.geometry.coordinates) {
                  const coords = r.geometry.coordinates[0];
                  if (Array.isArray(coords) && coords.length > 0) {
                    roomLocations = coords.map((coord: number[]) => {
                      if (Array.isArray(coord) && coord.length >= 2) {
                        return [coord[1], coord[0]]; // Convert [lon, lat] to [lat, lon]
                      }
                      return null;
                    }).filter((loc: any) => loc !== null) as number[][];
                  }
                }
                // Try to get from areaMapEntity
                if (roomLocations.length === 0 && r.id) {
                  const areaMapRoom = this.getAreaMapEntity(r.id);
                  if (areaMapRoom) {
                    if (typeof areaMapRoom.getCoordinates === 'function') {
                      const coords = areaMapRoom.getCoordinates();
                      if (Array.isArray(coords) && coords.length > 0) {
                        roomLocations = coords;
                      }
                    } else if (areaMapRoom.locations && Array.isArray(areaMapRoom.locations)) {
                      roomLocations = areaMapRoom.locations;
                    }
                  }
                }

                // Set locations on the room 
                if (roomLocations.length > 0) {
                  r.locations = roomLocations;
                }

                // Ensure name and arcAngelId
                if (!r.name && r.label) {
                  r.name = r.label;
                }
                if (!r.name && r.getName && typeof r.getName === 'function') {
                  r.name = r.getName();
                }
                if (!r.arcAngelId && r.id) {
                  r.arcAngelId = r.id;
                }

                map[String(r.id)] = r;
              }
            });
            originalFloor.perimeters = map;
          }
        }

        this.floorService.updateIncidentVisualizer(false);

        const floorMapEntity = originalFloor;

        // Update floor data with the FLOOR entity (not building entity) -
        this.floorService.updateFloorData(floorMapEntity);

        setTimeout(() => {
          this.floorService.toggleGrandchild(true);
        }, 100);
      }
    } else if (targetObject.userData['roomId'] || targetObject.userData['type'] === 'room') {
      // If clicking on a room, find its parent floor and highlight that
      const roomId = targetObject.userData['roomId'] || targetObject.userData['id'];
      const roomEntity = this.getAreaMapEntity(roomId);

      if (roomEntity && roomEntity.ancestors) {
        // Find the floor ancestor
        const floorAncestor = roomEntity.ancestors.find((ancestorId: string) => {
          const ancestor = this.getAreaMapEntity(ancestorId);
          return ancestor && ancestor.type === 'FLOOR';
        });

        if (floorAncestor) {
          // this.highlightFloorById(floorAncestor, true);
        }
      }

      if (this.cesiumThreeContainer) {
        this.onRoomClick(roomId);
      }
    } else if (targetObject.userData['buildingId']) {
      if (targetObject.userData['type'] === 'building-marker') {
        this.handleBuildingMarkerClick(targetObject.userData['buildingId'], targetObject);
      } else {
        this.onBuildingClick(targetObject.userData['buildingId']);
      }
    } else {
      // Clicked on empty space or non-floor object, remove highlight
      if (this.previouslyHighlightedFloor) {
        // this.highlightFloorById(this.previouslyHighlightedFloor, false);
      }
    }
  }

  public triggerBuildingMarkerClick(buildingId: string): void {
    if (!buildingId) {
      return;
    }

    if (!this.buildingModels) {
      this.buildingModels = new Map<string, THREE.Group>();
    }

    let buildingGroup = this.buildingModels.get(buildingId);

    if (!buildingGroup) {
      buildingGroup = new THREE.Group();
      buildingGroup.name = `BuildingGroup_${buildingId}`;

      if (!this.buildingContainerGroup) {
        this.buildingContainerGroup = new THREE.Group();
        this.buildingContainerGroup.name = 'BuildingsContainer';
        this.threeScene?.add(this.buildingContainerGroup);
      }

      this.buildingContainerGroup.add(buildingGroup);
      this.buildingModels.set(buildingId, buildingGroup);
    }

    const markerSprite = buildingGroup.userData?.['markerSprite'];

    if (markerSprite) {
      this.handleBuildingMarkerClick(buildingId, markerSprite);
    }
  }

  public async openBuilding(buildingEntity: BuildingMapEntity): Promise<void> {
    if (!buildingEntity?.id) return;

    if (!this.buildingModels) {
      this.buildingModels = new Map<string, THREE.Group>();
    }

    if (this.buildingModels.has(buildingEntity.id)) {
      this.triggerBuildingMarkerClick(buildingEntity.id);
      return;
    }

    const anchor =
      typeof buildingEntity['pin'].centerpoint.latitude === 'number' &&
        typeof buildingEntity['pin'].centerpoint.longitude === 'number'
        ? { lat: buildingEntity['pin'].centerpoint.latitude, lon: buildingEntity['pin'].centerpoint.longitude }
        : undefined;
    await this.activate3DBuildingViewFromPerimetersGeoJSON(
      [],
      anchor,
      buildingEntity
    );

    this.triggerBuildingMarkerClick(buildingEntity.id);
  }

  private handleBuildingMarkerClick(buildingId: string, object: THREE.Object3D) {
    const buildingEntity = this.buildingEntities.get(buildingId);
    if (!buildingEntity || !this.threeCamera || !this.threeRenderer) return;

    if (this.campusUserCountSubscription) {
      this.campusUserCountSubscription.unsubscribe();
      this.campusUserCountSubscription = null;
    }
    const buildingTopHeight = object.userData['height'];
    const worldPos = new THREE.Vector3();
    object.getWorldPosition(worldPos);
    const vector = worldPos.clone().project(this.threeCamera);
    const width = this.threeRenderer.domElement.clientWidth;
    const height = this.threeRenderer.domElement.clientHeight;
    const x = (vector.x * 0.5 + 0.5) * width;
    const y = (-vector.y * 0.5 + 0.5) * height;

    this.beacons = [];

    const floorMapEntities = buildingEntity.floorPerimeters;

    if (Array.isArray(floorMapEntities)) {

      floorMapEntities.forEach((floor: any) => {
        if (!floor?.perimeters) return;

        Object.values(floor.perimeters).forEach((perimeter: any) => {

          if (
            perimeter &&
            (perimeter.perimeterType === 'FLOOR' ||
              perimeter.type === 'FLOOR')
          ) return;

          if (!perimeter?.beacons) return;

          const beaconList = Array.isArray(perimeter.beacons)
            ? perimeter.beacons
            : [perimeter.beacons];

          beaconList.forEach((beaconWrapper: any) => {
            if (beaconWrapper && Object.keys(beaconWrapper).length > 0) {
              const beacon = Object.values(beaconWrapper)[0];
              this.beacons.push({
                ...(beacon as object),
                perimeter,
                floorId: floor.id,
                floorNumber: floor.floorNumber,
              });
            }
          });

        });
      });
    }
    const data = {
      buildingEntity,
      name: buildingEntity.name || buildingEntity.label || 'Building',
      address: buildingEntity.associatedcampus?.name || '',
      height: Math.round(buildingTopHeight || 0),
      floors: buildingEntity.floorPerimeters?.length || 0,
      beacons: this.beacons.length,
      people: [
        { color: 'text-danger', count: buildingEntity.dangerUserCount || 0 },
        { color: 'text-success', count: buildingEntity.safeUserCount || 0 },
        { color: 'text-warning', count: buildingEntity.unknownUserCount || 0 },
        { color: 'text-info', count: buildingEntity.suspectUserCount || 0 }
      ],
      alerts: buildingEntity?.getAlertCount() || 0
    };
    this.showBuildingPopup(object, data, x, y, buildingTopHeight);
    this.campusUserCountSubscription = interval(2000).pipe(
      withLatestFrom(this.buildingStateService.selectedBuilding$),
      filter(([_, selectedBuilding]) => !!selectedBuilding && selectedBuilding.buildingEntity?.id === buildingId)
    ).subscribe(() => {
      data.people = [
        { color: 'text-danger', count: buildingEntity.dangerUserCount || 0 },
        { color: 'text-success', count: buildingEntity.safeUserCount || 0 },
        { color: 'text-warning', count: buildingEntity.unknownUserCount || 0 },
        { color: 'text-info', count: buildingEntity.suspectUserCount || 0 }
      ];
      data.alerts = buildingEntity?.getAlertCount() || 0;
      this.zone.run(() => {
        this.buildingStateService.setSelectedBuilding({ ...data });
      });
    });
  }
  /**
   * Highlight a specific floor in the 3D view by floor ID or perimeter ID
   * @param floorId The floor ID (perimeterId, id, or label)
   * @param highlight Whether to highlight or unhighlight
   * @param syncBabylon When true, syncs Babylon/GLB floor highlight via BlenderBuildingService
   */
  highlightFloorById(floorId: string, highlight: boolean = true, syncBabylon: boolean = true): void {
    if (!this.threeScene || !floorId) {
      return;
    }

    if (highlight && this.previouslyHighlightedFloor === floorId) {
      return;
    }

    if (this.previouslyHighlightedFloor && this.previouslyHighlightedFloor !== floorId) {
      this.highlightFloorById(this.previouslyHighlightedFloor, false, syncBabylon);
    }

    const areaEntity = this.areaMapEntities.get(floorId);
    if (!areaEntity) {
      this.blenderBuilding.applyFloorHighlightSync(floorId, highlight, syncBabylon);
      return;
    }

    const targetAreaId: string = areaEntity.id;
    let foundFloor = false;

    const applyHighlight = (mesh: THREE.Mesh): void => {
      const material = mesh.material as
        | THREE.MeshStandardMaterial
        | THREE.MeshPhongMaterial
        | THREE.MeshLambertMaterial;

      if (!mesh.userData['originalMaterial']) {
        mesh.userData['originalMaterial'] = {
          color: material.color?.clone(),
          emissive: material.emissive?.clone() ?? new THREE.Color(0x000000),
          opacity: material.opacity ?? 1,
          transparent: material.transparent ?? false
        };
      }

      if (!mesh.userData['originalScale']) {
        mesh.userData['originalScale'] = mesh.scale.clone();
      }

      material.emissive = new THREE.Color(0x000000);
      if ('emissiveIntensity' in material) {
        material.emissiveIntensity = 0;
      }
    };

    const restoreHighlight = (mesh: THREE.Mesh): void => {
      const material = mesh.material as
        | THREE.MeshStandardMaterial
        | THREE.MeshPhongMaterial
        | THREE.MeshLambertMaterial;

      const original = mesh.userData['originalMaterial'];
      if (original) {
        if (original.color && material.color) {
          material.color.copy(original.color);
        }
        if (original.emissive && material.emissive) {
          material.emissive.copy(original.emissive);
        }
        material.opacity = original.opacity;
        material.transparent = original.transparent;
        if ('emissiveIntensity' in material) {
          material.emissiveIntensity = 0;
        }
      }

      if (mesh.userData['originalScale']) {
        mesh.scale.copy(mesh.userData['originalScale']);
      }
    };

    this.threeScene.traverse((object: THREE.Object3D) => {
      if (!(object instanceof THREE.Mesh)) {
        return;
      }

      const ud = object.userData ?? {};

      if (ud['type'] !== 'floor') {
        return;
      }

      if (ud['floorId'] !== targetAreaId) {
        return;
      }

      foundFloor = true;

      // if (highlight) {
      //   applyHighlight(object);
      // } else {
      //   restoreHighlight(object);
      // }
    });

    if (highlight && foundFloor) {
      this.previouslyHighlightedFloor = floorId;
    }

    if (!highlight) {
      this.previouslyHighlightedFloor = null;
    }

    this.blenderBuilding.applyFloorHighlightSync(floorId, highlight, syncBabylon);
  }
  /**
   * Focus on a specific area/floor
   */
  focusFloorOnArea(areaId: string, container: HTMLElement): void {
    const area = this.getAreaMapEntity(areaId);
    if (!area) return;

    // Detect floor number
    let floorNumber = 0;
    if (typeof area.getFloorNumber === 'function') {
      floorNumber = area.getFloorNumber() || 0;
    } else if (typeof area.level === 'number') {
      floorNumber = area.level;
    } else if (typeof area.label === 'string') {
      const match = area.label.match(/\d+/);
      floorNumber = match ? parseInt(match[0], 10) : 0;
    }

    // Ensure building data is available
    if (!this.currentAreaMapBuilding) {
      const allAreas = Array.from(this.areaMapEntities.values());
      this.currentAreaMapBuilding = this.createBuildingDataFromAreaMapEntities(allAreas);
    }

    // Raw floor geometry
    let previewFloorCoords: number[][] = [];
    if (area.locations) {
      let poly = area.locations;
      if (Array.isArray(poly[0]) && Array.isArray(poly[0][0])) {
        poly = poly[0];
      }
      previewFloorCoords = poly;
    }

    const allAreas = Array.from(this.areaMapEntities.values());

    // 1. Floor descendant IDs → rooms
    const descendantRoomIds = new Set(
      Array.isArray(area.descendants) ? area.descendants : []
    );

    // 2. Rooms from floor's perimeters
    const previewRooms: RoomData[] = Object.values(area.perimeters || {})
      .filter((p: any) => p.perimeterType === 'ROOM')
      .map((room: any, index) => {
        let roomCoords: number[][] = [];

        if (room.locations) {
          let poly = room.locations;
          if (Array.isArray(poly[0]) && Array.isArray(poly[0][0])) {
            poly = poly[0];
          }
          roomCoords = poly;
        }

        return {
          id: room.id || `room-${area.id}-${index}`,
          x: 0,
          z: 0,
          width: 10,
          length: 10,
          height: 3,
          color: 0xf5f5f5,
          name: room.label || `Room ${index + 1}`,
          coordinates: roomCoords
        };
      });

    // Final preview struct
    const previewFloorData: FloorData = {
      id: area.id,
      name: area.label || `Floor ${floorNumber}`,
      level: floorNumber,
      height: 3,
      rooms: previewRooms,
      coordinates: previewFloorCoords
    };

  }
  /**
    * Create building data from area map entities
    */
  createBuildingDataFromAreaMapEntities(areas: any[], buildingInfo?: any): AreaMapBuildingData {
    if (!areas || areas.length === 0) {
      throw new Error('No area map entities provided');
    }

    // Detect building
    const mainArea =
      areas.find(area => typeof area.getParentBuilding === 'function' && area.getParentBuilding()) ||
      areas[0];

    const building =
      (typeof mainArea.getParentBuilding === 'function'
        ? mainArea.getParentBuilding()
        : undefined) || buildingInfo;

    // Compute bounding box
    let minLat = Infinity,
      maxLat = -Infinity;
    let minLon = Infinity,
      maxLon = -Infinity;
    let totalHeight = 0;

    areas.forEach(area => {
      let coords: any = undefined;

      if (typeof area.getCoordinates === 'function') {
        coords = area.getCoordinates();
      } else if (area.geometry && Array.isArray(area.geometry.coordinates)) {
        coords = area.geometry.coordinates;
      }

      if (coords && Array.isArray(coords)) {
        let poly = coords;
        if (Array.isArray(coords[0]) && Array.isArray(coords[0][0])) {
          poly = coords[0];
        }
        poly.forEach((coord: any) => {
          if (!Array.isArray(coord) || coord.length < 2) return;
          const [lon, lat] = coord;
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);
          minLon = Math.min(minLon, lon);
          maxLon = Math.max(maxLon, lon);
        });
      }

      totalHeight = Math.max(
        totalHeight,
        typeof area.getAltitude === 'function' ? area.getAltitude() || 0 : 0
      );
    });

    const validLat = isFinite(minLat) && isFinite(maxLat);
    const validLon = isFinite(minLon) && isFinite(maxLon);

    const centerLat = validLat ? (minLat + maxLat) / 2 : 0;
    const centerLon = validLon ? (minLon + maxLon) / 2 : 0;

    const width = validLat && validLon ? this.calculateDistance(minLat, minLon, minLat, maxLon) : 100;
    const length = validLat && validLon ? this.calculateDistance(minLat, minLon, maxLat, minLon) : 100;

    const floors: FloorData[] = areas
      .filter(area => area.type === 'FLOOR')
      .map((area, areaIndex) => {
        // Determine floor number
        let floorNumber = areaIndex;

        if (typeof area.getFloorNumber === 'function') {
          floorNumber = area.getFloorNumber() || areaIndex;
        } else if (typeof area.level === 'number') {
          floorNumber = area.level;
        } else if (typeof area.label === 'string') {
          const match = area.label.match(/\d+/);
          floorNumber = match ? parseInt(match[0], 10) : areaIndex;
        }

        // Floor geometry → local coordinates
        let floorCoords: any = undefined;

        if (typeof area.getCoordinates === 'function') {
          floorCoords = area.getCoordinates();
        } else if (area.geometry?.coordinates) {
          floorCoords = area.geometry.coordinates;
        }

        let floorCoordinates: number[][] | undefined = undefined;

        if (Array.isArray(floorCoords)) {
          let poly = floorCoords;
          if (Array.isArray(poly[0]) && Array.isArray(poly[0][0])) {
            poly = poly[0];
          }

          floorCoordinates = poly.map((coord: any) => {
            if (!Array.isArray(coord)) return [0, 0];
            const [lon, lat] = coord;
            const pt = this.latLonToLocalVector2(lon, lat, centerLon, centerLat, 1);
            return [pt.x, pt.y];
          });
        }

        // 1. Rooms listed in floor.descendants
        const descendantRoomIds = new Set(
          Array.isArray(area.descendants) ? area.descendants : []
        );

        // 2. Rooms that have ancestors pointing to this floor
        const rooms: RoomData[] = areas
          .filter(roomArea =>
            roomArea.type === 'ROOM' &&
            (
              descendantRoomIds.has(roomArea.id) || // new logic
              (Array.isArray(roomArea.ancestors) && roomArea.ancestors.includes(area.id)) // existing logic
            )
          )
          .map((roomArea, index) => {
            // Convert room geometry
            let roomCoords: any = undefined;

            if (roomArea.geometry?.coordinates) {
              roomCoords = roomArea.geometry.coordinates;
            }

            let coordinates: number[][] | undefined = undefined;

            if (Array.isArray(roomCoords)) {
              let poly = roomCoords;
              if (Array.isArray(poly[0]) && Array.isArray(poly[0][0])) {
                poly = poly[0];
              }

              coordinates = poly.map((coord: any) => {
                const [lon, lat] = coord;
                const pt = this.latLonToLocalVector2(lon, lat, centerLon, centerLat, 1);
                return [pt.x, pt.y];
              });
            }

            // Compute bounding box for fallback
            let roomBounds = { minX: 0, maxX: 10, minZ: 0, maxZ: 10 };

            if (coordinates?.length) {
              const xs = coordinates.map(c => c[0]);
              const ys = coordinates.map(c => c[1]);

              roomBounds = {
                minX: Math.min(...xs),
                maxX: Math.max(...xs),
                minZ: Math.min(...ys),
                maxZ: Math.max(...ys)
              };
            }

            return {
              id: roomArea.id || `room-${area.id}-${index}`,
              x: roomBounds.minX,
              z: roomBounds.minZ,
              width: Math.abs(roomBounds.maxX - roomBounds.minX) || 10,
              length: Math.abs(roomBounds.maxZ - roomBounds.minZ) || 10,
              height: 3,
              color: 0xf5f5f5,
              name: roomArea.label || `Room ${index + 1}`,
              coordinates
            };
          });

        return {
          id: area.id,
          name: area.label || `Floor ${floorNumber}`,
          level: floorNumber,
          height: 3,
          rooms,
          coordinates: floorCoordinates
        };
      });

    return {
      id: building?.id || mainArea.id,
      name: building?.name || building?.label || mainArea.label || 'Building',
      longitude: centerLon,
      latitude: centerLat,
      height: totalHeight || 30,
      width,
      length,
      floors,
      totalRooms: floors.reduce((sum, f) => sum + (f.rooms?.length || 0), 0),
      areas,
      areaMapEntity: mainArea,
      perimeters: areas.flatMap(area => {
        if (typeof area.getPerimeters === 'function') {
          return Object.values(area.getPerimeters());
        }
        return [];
      })
    };
  }
  getAreaMapEntity(areaId: string): any {
    return this.areaMapEntities.get(areaId);
  }
  /**
   * Calculate distance between two coordinates in meters
   */
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }
  /**
   * Handle building click events
   */
  private onBuildingClick(buildingId: string): void { }

  /**
   * Handle room click events
   */
  private onRoomClick(roomId: string,): void {
    const previewContainer = document.getElementById('threejs-floor-preview');
    if (previewContainer) {
      // Find the floor ID for this room
      const roomEntity = this.getAreaMapEntity(roomId);
      if (roomEntity && roomEntity.ancestors) {
        const floorId = roomEntity.ancestors.find((ancestorId: string) => {
          const ancestor = this.getAreaMapEntity(ancestorId);
          return ancestor && ancestor.type === 'FLOOR';
        });

        if (floorId) {
          this.focusFloorOnArea(floorId, previewContainer);
        }
      }
    }
  }
  /**
   * Dispose of Three.js resources
   */
  dispose(): void {
    this.deactivate3DBuildingView();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.controls) {
      this.controls.dispose();
      this.controls = null;
    }

    if (this.threeRenderer) {
      this.threeRenderer.dispose();
      if (this.threeRenderer.domElement.parentNode) {
        this.threeRenderer.domElement.parentNode.removeChild(this.threeRenderer.domElement);
      }
      this.threeRenderer = null;
    }

    if (this.threeScene) {
      this.threeScene.clear();
      this.threeScene = null;
    }

    if (this.tooltipDiv && this.tooltipDiv.parentNode) {
      this.tooltipDiv.parentNode.removeChild(this.tooltipDiv);
      this.tooltipDiv = null;
    }

    this.threeCamera = null;
    this.buildingModels.clear();

    window.removeEventListener('resize', this.onWindowResize.bind(this));
  }
  /**
    * Deactivate 3D building view
    */
  deactivate3DBuildingView(): void {
    this.blenderBuilding.hideGlbMapOverlay();
    if (this.campusUserCountSubscription) {
      this.campusUserCountSubscription.unsubscribe();
      this.campusUserCountSubscription = null;
    }
    this.isThreeJSActive = false;
    this.currentBuildingPosition = null;
    this.clearBuildingModels();

    // Clear area map references
    this.currentAreaMapBuilding = null;
    this.areaMapEntities.clear();
    this.buildingEntities.clear();
    this.buildingGeographicCenter = null;

    // Clear status spheres
    ThreeIntegrationService.statusIndicators.forEach(indicator => {
      this.threeScene?.remove(indicator);
      (indicator.material as THREE.Material).dispose();
    });
    ThreeIntegrationService.statusIndicators.clear();

    // Clear Three.js scene (main building scene only)
    if (this.threeScene && this.buildingContainerGroup) {
      this.threeScene.remove(this.buildingContainerGroup);
      this.buildingContainerGroup.clear();
      this.buildingContainerGroup = null;
      if (this.threeScene.children.filter(c => c.type.includes('Light')).length === 0) {
        this.setupThreeJSScene();
      }
    }

    // Hide main Three.js renderer — NOT the preview renderer
    if (this.threeRenderer && this.cesiumThreeContainer) {
      this.threeRenderer.domElement.style.display = 'none';
    }
    if (this.previewRenderer) {
      this.previewRenderer.dispose();
      this.previewRenderer = null;
    }

    if (this.previewAnimationId) {
      cancelAnimationFrame(this.previewAnimationId);
      this.previewAnimationId = null;
    }
    if (this.previewControls) {
      this.previewControls.dispose();
      this.previewControls = null;
    }
    const previewContainer = document.getElementById('threejs-floor-preview');
    if (previewContainer) {
      while (previewContainer.firstChild) {
        previewContainer.removeChild(previewContainer.firstChild);
      }
    }

    // Stop main animation loop — NOT the preview animation loop
    const viewer = this.coresService.getViewer();
    if (viewer && this.renderLoopCallback) {
      viewer.scene.postRender.removeEventListener(this.renderLoopCallback);
    }
  }
  /**
   * Clear all building models
   */
  private clearBuildingModels(): void {
    this.buildingModels.forEach((model) => {
      if (this.threeScene) {
        this.threeScene.remove(model);
      }
    });
    this.buildingModels.clear();
  }
  private lastRendererWidth = 0;
  private lastRendererHeight = 0;

  /**
  * Resize Three.js renderer
  */
  resizeThreeJSRenderer(): void {
    if (!this.threeRenderer || !this.threeCamera || !this.cesiumThreeContainer) return;

    const width = this.cesiumThreeContainer.clientWidth;
    const height = this.cesiumThreeContainer.clientHeight;

    if (width === 0 || height === 0) {
      return;
    }

    if (width === this.lastRendererWidth && height === this.lastRendererHeight) {
      return;
    }
    this.lastRendererWidth = width;
    this.lastRendererHeight = height;

    this.threeRenderer.setSize(width, height);
    this.threeCamera.aspect = width / height;
    this.threeCamera.updateProjectionMatrix();
  }
  /**
    * Synchronize Three.js camera with Cesium camera in real-time
    */
  private synchronizeCameras(): void {
    if (!this.threeCamera || !this.buildingGeographicCenter) {
      return;
    }
    if (!this.isThreeJSActive && !this.blenderBuilding.isGlbMapOverlayActive()) {
      return;
    }

    const viewer = this.coresService.getViewer();
    if (!viewer) return;

    const cesiumCamera = viewer.camera;

    // FOV & aspect sync
    if (cesiumCamera.frustum instanceof Cesium.PerspectiveFrustum) {
      this.threeCamera.fov = Cesium.Math.toDegrees(cesiumCamera.frustum.fovy);
      this.threeCamera.aspect = cesiumCamera.frustum.aspectRatio;
    }
    this.threeCamera.updateProjectionMatrix();

    // Compute ENU offset
    const buildingCartesian = Cesium.Cartesian3.fromDegrees(
      this.buildingGeographicCenter.lon,
      this.buildingGeographicCenter.lat,
      0
    );

    const enuToFixed = Cesium.Transforms.eastNorthUpToFixedFrame(buildingCartesian);
    const fixedToEnu = Cesium.Matrix4.inverse(enuToFixed, new Cesium.Matrix4());

    const cameraToFixed = cesiumCamera.inverseViewMatrix;

    const cameraToEnu = Cesium.Matrix4.multiply(fixedToEnu, cameraToFixed, new Cesium.Matrix4());

    const m = Cesium.Matrix4.toArray(cameraToEnu);

    const threeMatrix = new THREE.Matrix4();
    threeMatrix.set(
      m[0], m[4], m[8], m[12],
      m[2], m[6], m[10], m[14],
      -m[1], -m[5], -m[9], -m[13],
      0, 0, 0, 1
    );

    this.threeCamera.matrixAutoUpdate = false;
    this.threeCamera.matrix.copy(threeMatrix);
    this.threeCamera.updateMatrixWorld(true);
  }

  public async activate3DBuildingViewFromPerimetersGeoJSON(
    geojsonArray: any[],
    anchor?: { lat: number; lon: number },
    buildingEntity?: BuildingMapEntity
  ): Promise<void> {

    if (!this.threeScene || !this.controls || !this.threeCamera) {
      console.error('Three.js not initialized');
      return;
    }

    if (!this.buildingContainerGroup) {
      this.buildingContainerGroup = new THREE.Group();
      this.buildingContainerGroup.name = 'BuildingsContainer';
      this.threeScene.add(this.buildingContainerGroup);
    }

    const floors = geojsonArray
      .filter(f => f.isFloorPerimeter && Array.isArray(f.locations))
      .sort((a, b) => (a.floorNumber ?? 0) - (b.floorNumber ?? 0));

    if (!floors.length) {
      const buildingGroup = new THREE.Group();
      buildingGroup.name = `BuildingGroup_${buildingEntity?.id || Date.now()}`;

      if (!this.buildingContainerGroup) {
        this.buildingContainerGroup = new THREE.Group();
        this.buildingContainerGroup.name = 'BuildingsContainer';
        this.threeScene?.add(this.buildingContainerGroup);
      }

      if (anchor) {
        let localCenter = this.calculateBuildingCenterFromGeoJSON(floors);
        if (anchor) localCenter = anchor;

        if (!this.buildingGeographicCenter) {
          this.buildingGeographicCenter = { ...localCenter };
        }

        const globalCenter = this.buildingGeographicCenter;

        // Get ground height at building location to align with terrain
        const viewer = this.coresService.getViewer();
        let groundHeight = 0;
        if (viewer) {
          const cartographic = Cesium.Cartographic.fromDegrees(globalCenter.lon, globalCenter.lat);
          groundHeight = viewer.scene.globe.getHeight(cartographic) || 0;
        }

        const centerCartesian = Cesium.Cartesian3.fromDegrees(globalCenter.lon, globalCenter.lat, groundHeight);
        const targetCartesian = Cesium.Cartesian3.fromDegrees(localCenter.lon, localCenter.lat, groundHeight);
        const centerEnu = Cesium.Transforms.eastNorthUpToFixedFrame(centerCartesian);
        const targetEnu = Cesium.Transforms.eastNorthUpToFixedFrame(targetCartesian);
        const invCenterEnu = Cesium.Matrix4.inverse(centerEnu, new Cesium.Matrix4());
        const transformCesium = Cesium.Matrix4.multiply(invCenterEnu, targetEnu, new Cesium.Matrix4());
        const m = Cesium.Matrix4.toArray(transformCesium);
        const matrix = new THREE.Matrix4();
        matrix.set(
          m[0], m[8], -m[4], m[12], // Row 0: X_t = X_c, Y_t = Z_c, Z_t = -Y_c
          m[2], m[10], -m[6], m[14], // Row 1
          -m[1], -m[9], m[5], -m[13], // Row 2
          0, 0, 0, 1
        );

        buildingGroup.matrixAutoUpdate = false;
        buildingGroup.matrix.copy(matrix);
        buildingGroup.updateMatrixWorld(true);

        const worldPosition = new THREE.Vector3();
        worldPosition.setFromMatrixPosition(buildingGroup.matrix);
        buildingGroup.position.copy(worldPosition);
      }

      this.buildingContainerGroup.add(buildingGroup);

      if (!this.buildingModels) {
        this.buildingModels = new Map<string, THREE.Group>();
      }

      if (!this.buildingEntities) {
        this.buildingEntities = new Map<string, BuildingMapEntity>();
      }

      if (buildingEntity?.id) {
        this.buildingEntities.set(buildingEntity.id, buildingEntity);

        this.buildingModels.set(buildingEntity.id, buildingGroup);

        buildingEntity.setThreeJsMesh?.(buildingGroup);
      }
      const material = new THREE.SpriteMaterial({

        transparent: true,
        depthTest: false,
        depthWrite: false
      });
      const sprite = new THREE.Sprite(material);
      if (buildingEntity?.id) {
        sprite.userData = {
          type: 'building-marker',
          buildingId: buildingEntity?.id,
          height: 0
        };
      }

      buildingGroup.userData['markerSprite'] = sprite;

      buildingGroup.add(sprite);
      return;
    }

    let localCenter = this.calculateBuildingCenterFromGeoJSON(floors);
    if (anchor) localCenter = anchor;

    if (!this.buildingGeographicCenter) {
      this.buildingGeographicCenter = { ...localCenter };
    }
    const globalCenter = this.buildingGeographicCenter;

    const centerCartesian = Cesium.Cartesian3.fromDegrees(globalCenter.lon, globalCenter.lat);
    const targetCartesian = Cesium.Cartesian3.fromDegrees(localCenter.lon, localCenter.lat);
    const centerEnu = Cesium.Transforms.eastNorthUpToFixedFrame(centerCartesian);
    const targetEnu = Cesium.Transforms.eastNorthUpToFixedFrame(targetCartesian);
    const invCenterEnu = Cesium.Matrix4.inverse(centerEnu, new Cesium.Matrix4());
    const transformCesium = Cesium.Matrix4.multiply(invCenterEnu, targetEnu, new Cesium.Matrix4());

    const buildingGroup = new THREE.Group();
    buildingGroup.name = `BuildingGroup_${buildingEntity?.id || floors[0]?.id || Date.now()}`;

    const scale = 1;

    floors.forEach((floor) => {
      if (floor.id) {
        this.areaMapEntities.set(floor.id, floor);
      }

      const bottom = floor.height ?? 0;
      const top = floor.extrudedHeight ?? bottom + 3.5;
      const height = Math.max(0.1, top - bottom);

      const shape = new THREE.Shape();

      floor.locations.forEach(([lat, lon]: number[], i: number) => {
        const v = this.latLonToLocalVector2(
          lon,
          lat,
          localCenter.lon,
          localCenter.lat,
          scale
        );

        if (i === 0) shape.moveTo(v.x, v.y);
        else shape.lineTo(v.x, v.y);
      });

      const geometry = new THREE.ExtrudeGeometry(shape, {
        depth: height + 3,
        bevelEnabled: false
      });

      geometry.translate(0, 0, -height / 2);
      geometry.rotateX(-Math.PI / 2);

      const mesh = new THREE.Mesh(
        geometry,
        new THREE.MeshPhongMaterial({
          color: 0xffffff,
          emissive: 0x222222,
          specular: 0xffffff,
          shininess: 90,
          transparent: true,
          opacity: 0.15,
          depthWrite: false,
          side: THREE.DoubleSide
        })
      );

      const floorEdges = new THREE.EdgesGeometry(geometry);
      const floorBorderMaterial = new THREE.LineBasicMaterial({
        color: 0x666666,
        transparent: true,
        opacity: 0.4,
        depthWrite: false
      });
      const floorBorder = new THREE.LineSegments(floorEdges, floorBorderMaterial);
      mesh.add(floorBorder);

      mesh.position.y = bottom + height / 2;

      mesh.userData = {
        type: 'floor',
        label: floor.label || `Floor ${floor.floorNumber}`,
        floorId: floor.id,
        floorNumber: floor.floorNumber
      };

      buildingGroup.add(mesh);
    });

    floors.forEach((floor, floorIdx) => {

      const bottom = floor.height ?? 0;
      const top = floor.extrudedHeight ?? bottom + 3.5;
      const height = Math.max(0.1, top - bottom);

      Object.values(floor.perimeters || {})
        .filter((p: any) => p.perimeterType === 'ROOM')
        .forEach((room: any) => {

          if (!room.locations?.length) return;

          const shape = new THREE.Shape();
          const roomId = room.id || room.label;

          this.areaMapEntities.set(roomId, room);

          room.locations.forEach(([lat, lon]: number[], i: number) => {
            const v = this.latLonToLocalVector2(
              lon,
              lat,
              localCenter.lon,
              localCenter.lat,
              scale
            );

            if (i === 0) shape.moveTo(v.x, v.y);
            else shape.lineTo(v.x, v.y);
          });

          const geometry = new THREE.ExtrudeGeometry(shape, {
            depth: height + 3,
            bevelEnabled: false
          });

          geometry.translate(0, 0, -height / 2);
          geometry.rotateX(-Math.PI / 2);

          const baseMaterial = new THREE.MeshPhongMaterial({
            color: 0xffffff,
            emissive: 0x111111,
            specular: 0xffffff,
            shininess: 80,
            transparent: true,
            opacity: 0.06,
            depthWrite: false,
            side: THREE.DoubleSide
          });

          const mesh = new THREE.Mesh(geometry, baseMaterial);

          const edges = new THREE.EdgesGeometry(geometry);
          const borderMaterial = new THREE.LineBasicMaterial({
            color: 0x444444,
            transparent: true,
            opacity: 0.3,
            depthWrite: false,
            linewidth: 1
          });

          const border = new THREE.LineSegments(edges, borderMaterial);
          border.userData['originalColor'] = borderMaterial.color.clone();

          mesh.add(border);

          mesh.position.y = bottom + height / 2;
          mesh.userData = {
            type: 'room',
            id: roomId,
            label: room.name || `Room ${roomId}`,
            floorNumber: floor.floorNumber,
            originalMaterial: baseMaterial,
            border: border
          };

          ThreeIntegrationService.roomMeshMap.set(roomId, mesh);
          buildingGroup.add(mesh);
        });
    });
    const buildingTopHeight = Math.max(
      ...floors.map(f => f.extrudedHeight ?? (f.height ?? 0) + 3.5)
    );

    await this.loadBuildingIcon();
    this.buildingContainerGroup.add(buildingGroup);
    this.addBuildingMarkerCard(
      buildingGroup,
      buildingTopHeight,
      buildingEntity?.id
    );
    const m = Cesium.Matrix4.toArray(transformCesium);
    const matrix = new THREE.Matrix4();
    matrix.set(
      m[0], m[8], -m[4], m[12], // Row 0: X_t = X_c, Y_t = Z_c, Z_t = -Y_c
      m[2], m[10], -m[6], m[14], // Row 1
      -m[1], -m[9], m[5], -m[13], // Row 2
      0, 0, 0, 1
    );

    buildingGroup.matrixAutoUpdate = false;
    buildingGroup.matrix.copy(matrix);
    buildingGroup.updateMatrixWorld(true);

    if (buildingEntity) {
      this.buildingEntities.set(buildingEntity.id, buildingEntity);
      buildingEntity.setThreeJsMesh(buildingGroup);
      this.buildingModels.set(buildingEntity.id, buildingGroup);
    }

    if (this.controls.target.lengthSq() === 0) {
      this.controls.target.set(0, 0, 0);
      this.controls.update();
    }

    this.isThreeJSActive = true;

    if (this.threeRenderer && this.cesiumThreeContainer) {
      this.threeRenderer.domElement.style.display = '';
      this.resizeThreeJSRenderer();
    }

    this.startRenderLoop();

    this.synchronizeCameras();
    if (ThreeIntegrationService.roomMeshMap.size > 0) {
      this.blenderBuilding.notifyWireframeReady();
    }
  }

  private calculateBuildingCenterFromGeoJSON(geojsonArray: any[]): { lat: number; lon: number } {

    let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
    let coordCount = 0;

    geojsonArray.forEach(area => {

      for (const coord of area.locations) {
        if (!Array.isArray(coord) || coord.length < 2) continue;

        const [lat, lon] = coord;

        if (
          typeof lon === 'number' &&
          typeof lat === 'number' &&
          !isNaN(lon) &&
          !isNaN(lat)
        ) {
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);
          minLon = Math.min(minLon, lon);
          maxLon = Math.max(maxLon, lon);
          coordCount++;
        }
      }
    });

    if (coordCount === 0 || minLat === 90 || maxLat === -90 || minLon === 180 || maxLon === -180) {
      return { lat: 0, lon: 0 };
    }

    const center = {
      lat: (minLat + maxLat) / 2,
      lon: (minLon + maxLon) / 2
    };

    return center;
  }

  private latLonToLocalVector2(lon: number, lat: number, centerLon: number, centerLat: number, scale: number): THREE.Vector2 {
    // Validate inputs
    if (typeof lon !== 'number' || typeof lat !== 'number' ||
      typeof centerLon !== 'number' || typeof centerLat !== 'number' ||
      isNaN(lon) || isNaN(lat) || isNaN(centerLon) || isNaN(centerLat)) {
      return new THREE.Vector2(0, 0);
    }


    const center = Cesium.Cartesian3.fromDegrees(centerLon, centerLat);
    const target = Cesium.Cartesian3.fromDegrees(lon, lat);

    const enu = Cesium.Transforms.eastNorthUpToFixedFrame(center);
    const invEnu = Cesium.Matrix4.inverse(enu, new Cesium.Matrix4());

    const offset = Cesium.Cartesian3.subtract(target, center, new Cesium.Cartesian3());
    const local = Cesium.Matrix4.multiplyByPointAsVector(invEnu, offset, new Cesium.Cartesian3());

    const x = local.x * scale;
    const y = local.y * scale;

    if (isNaN(x) || isNaN(y) || !isFinite(x) || !isFinite(y)) {
      return new THREE.Vector2(0, 0);
    }

    return new THREE.Vector2(x, y);
  }
  public activate3DVisualizationForBuilding(
    buildingEntities: any
  ): void {

    const buildings = Object.values(buildingEntities) as BuildingMapEntity[];
    const cesiumContainer = document.getElementById('cesiumContainer');

    if (cesiumContainer && this.cesiumThreeContainer !== cesiumContainer) {
      if (this.isThreeJSActive) {
        this.deactivate3DBuildingView();
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      this.initializeThreeJSIntegration(cesiumContainer);
    } else if (!this.isThreeJSActive) {
      if (cesiumContainer) {
        this.initializeThreeJSIntegration(cesiumContainer);
      } else {
        console.error('ThreeIntegrationService: cesiumContainer not found');
        // return;
      }
    }

    // Cleanup existing scene content
    if (this.buildingContainerGroup) {
      this.threeScene?.remove(this.buildingContainerGroup);
      this.buildingContainerGroup = null;
    }
    ThreeIntegrationService.roomMeshMap.clear();
    this.areaMapEntities.clear();
    this.clearBuildingModels();
    this.buildingGeographicCenter = null;

    // Find anchor (first building with valid location)
    let anchorBuilding = buildings.find(b => b['pin']?.centerpoint?.latitude);
    if (!anchorBuilding && buildings.length > 0) anchorBuilding = buildings[0];

    if (!anchorBuilding) return;

    // Set global center for the scene
    if (anchorBuilding['pin']?.centerpoint?.latitude) {
      this.buildingGeographicCenter = {
        lat: anchorBuilding['pin'].centerpoint.latitude,
        lon: anchorBuilding['pin'].centerpoint.longitude
      };
    } else if (anchorBuilding.floorPerimeters) {
      this.buildingGeographicCenter = this.calculateBuildingCenterFromGeoJSON(anchorBuilding.floorPerimeters);
    }

    this.buildingContainerGroup = new THREE.Group();
    this.buildingContainerGroup.name = 'BuildingsContainer';
    this.threeScene?.add(this.buildingContainerGroup);

    for (const building of buildings) {
      if (!building.floorPerimeters || building.floorPerimeters.length === 0) {
        continue;
      }

      const anchor =
        typeof building['pin'].centerpoint.latitude === 'number' &&
          typeof building['pin'].centerpoint.longitude === 'number'
          ? { lat: building['pin'].centerpoint.latitude, lon: building['pin'].centerpoint.longitude }
          : undefined;

      this.activate3DBuildingViewFromPerimetersGeoJSON(
        building.floorPerimeters,
        anchor,
        building
      );

    }
  }

  public resetSafetyStatusByStats(perimeter: any): void {
    if (!perimeter?.id) return;
    const mesh = ThreeIntegrationService.roomMeshMap.get(perimeter.id);
    if (!mesh) return;

    mesh.userData['perimeterSafetyStatus'] = this.PERIMETER_SAFETY_STATUS_SAFE;
    mesh.userData['perimeterUserCountStatus'] = this.PERIMETER_USER_COUNT_STATUS_NO_USERS;

    this.setMaterialFromStatus(perimeter);
  }

  public setSafetyStatusByStats(perimeter: any, status: string): void {
    if (!perimeter?.id) return;
    const mesh = ThreeIntegrationService.roomMeshMap.get(perimeter.id);
    if (!mesh) return;

    let safetyStatus = this.PERIMETER_SAFETY_STATUS_SAFE;
    if (status === 'unknown') {
      safetyStatus = this.PERIMETER_SAFETY_STATUS_UNKNOWN;
    } else if (status === 'unsafe') {
      safetyStatus = this.PERIMETER_SAFETY_STATUS_UNSAFE;
    } else if (status === 'safe') {
      safetyStatus = this.PERIMETER_SAFETY_STATUS_SAFE;
    }

    mesh.userData['perimeterSafetyStatus'] = safetyStatus;
    mesh.userData['perimeterUserCountStatus'] = this.PERIMETER_USER_COUNT_STATUS_SOME_USERS;

    this.setMaterialFromStatus(perimeter);
  }

  public setMaterialFromStatus(perimeter: any): void {
    if (!this.isThreeJSActive || !perimeter?.id) return;
    const mesh = ThreeIntegrationService.roomMeshMap.get(perimeter.id);
    if (!mesh) return;

    const safetyStatus = mesh.userData['perimeterSafetyStatus'];
    const userCountStatus = mesh.userData['perimeterUserCountStatus'];

    // Clean old sprite
    if (ThreeIntegrationService.statusIndicators.has(perimeter.id)) {
      const oldSprite = ThreeIntegrationService.statusIndicators.get(perimeter.id)!;
      oldSprite.parent?.remove(oldSprite);
      (oldSprite.material as THREE.Material).dispose();
      ThreeIntegrationService.statusIndicators.delete(perimeter.id);
    }

    if (userCountStatus === this.PERIMETER_USER_COUNT_STATUS_SOME_USERS) {
      let color = 0x808080;

      if (safetyStatus === this.PERIMETER_SAFETY_STATUS_UNSAFE) {
        color = 0xAA0000; // 170, 0, 0
      } else if (safetyStatus === this.PERIMETER_SAFETY_STATUS_UNKNOWN) {
        color = 0xFFE600; // 255, 230, 0
      } else if (safetyStatus === this.PERIMETER_SAFETY_STATUS_SAFE) {
        color = 0x008000; // 0, 128, 0
      }

      this.highlightRoom(mesh, color, 1);

      // Add sprite
      const texture = new THREE.TextureLoader().load(
        'assets/img/arcangel/user-status.svg'
      );

      const spriteMaterial = new THREE.SpriteMaterial({
        map: texture,
        color: color,
        transparent: true,
        depthTest: false,
        depthWrite: false
      });

      const sprite = new THREE.Sprite(spriteMaterial);
      sprite.scale.set(1.2, 1.2, 1);

      const box = new THREE.Box3().setFromObject(mesh);
      const centerWorld = box.getCenter(new THREE.Vector3());
      mesh.worldToLocal(centerWorld);

      sprite.position.copy(centerWorld);
      sprite.position.y += 0.2;

      mesh.add(sprite);
      ThreeIntegrationService.statusIndicators.set(perimeter.id, sprite);

    } else {
      this.resetRoomHighlight(mesh);
    }
  }

  public updatePerimeterStatusVisual(
    perimeter: any,
    status: 'safe' | 'unsafe' | 'unknown' | 'gray'
  ): void {

    if (!this.isThreeJSActive || !this.threeScene || !perimeter?.id) {
      return;
    }

    const perimeterId = perimeter.id;

    /* ================= CLEAN OLD SPRITE ================= */
    if (ThreeIntegrationService.statusIndicators.has(perimeterId)) {
      const oldSprite = ThreeIntegrationService.statusIndicators.get(perimeterId)!;
      oldSprite.parent?.remove(oldSprite);
      (oldSprite.material as THREE.Material).dispose();
      ThreeIntegrationService.statusIndicators.delete(perimeterId);
    }

    const roomMesh = ThreeIntegrationService.roomMeshMap.get(perimeterId);
    if (!roomMesh) {
      return;
    }

    if (status === 'gray') {
      this.resetRoomHighlight(roomMesh);
      return;
    }

    /* ================= STATUS COLOR ================= */
    let color: number;
    switch (status) {
      case 'unsafe': color = 0xff0000; break;
      case 'unknown': color = 0xffff00; break;
      case 'safe': color = 0x00ff00; break;
      default: return;
    }

    this.highlightRoom(roomMesh, color, 1);

    const texture = new THREE.TextureLoader().load(
      'assets/img/arcangel/user-status.svg'
    );

    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      color,
      transparent: true,
      depthTest: false,
      depthWrite: false
    });

    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.scale.set(1.2, 1.2, 1);

    const box = new THREE.Box3().setFromObject(roomMesh);
    const centerWorld = box.getCenter(new THREE.Vector3());
    roomMesh.worldToLocal(centerWorld);

    sprite.position.copy(centerWorld);
    sprite.position.y += 0.2;

    roomMesh.add(sprite);
    ThreeIntegrationService.statusIndicators.set(perimeterId, sprite);
  }

  private highlightRoom(
    roomMesh: THREE.Mesh,
    color: number,
    opacity = 1
  ): void {

    if (!roomMesh.userData['highlightMaterial']) {
      const highlightMat = new THREE.MeshBasicMaterial({
        color: color,
        side: THREE.DoubleSide,
        transparent: false,
        opacity: 1
      });
      roomMesh.userData['highlightMaterial'] = highlightMat;
    }

    const mat = roomMesh.userData['highlightMaterial'] as THREE.MeshBasicMaterial;
    mat.color.setHex(color);
    mat.opacity = 1;
    mat.transparent = false;

    roomMesh.material = mat;
  }
  private resetRoomHighlight(roomMesh: THREE.Mesh): void {

    if (roomMesh.userData['originalMaterial']) {
      roomMesh.material = roomMesh.userData['originalMaterial'];
    }

    const border = roomMesh.userData['border'] as THREE.LineSegments;
    if (border?.userData['originalColor']) {
      (border.material as THREE.LineBasicMaterial)
        .color.copy(border.userData['originalColor']);
    }
  }

  /** Reset all room occupancy highlights/sprites (e.g. when incident ends in Monitor Mode). */
  public clearAllRoomOccupancyVisuals(): void {
    ThreeIntegrationService.roomMeshMap.forEach((mesh) => {
      this.resetRoomHighlight(mesh);
    });
    ThreeIntegrationService.statusIndicators.forEach((sprite, id) => {
      sprite.parent?.remove(sprite);
      (sprite.material as THREE.Material).dispose();
      ThreeIntegrationService.statusIndicators.delete(id);
    });
  }

  /**
   * Check if Three.js is active
   */
  isActive(): boolean {
    return this.isThreeJSActive;
  }


  /**
   * Clear floor highlight
   */
  clearFloorHighlight(): void {
    if (this.previouslyHighlightedFloor) {
      this.highlightFloorById(this.previouslyHighlightedFloor, false);
      this.previouslyHighlightedFloor = null;
    }
  }

  /**
   * Highlight multiple floors for alert visualization in 3D mode.
   * This is intentionally Babylon-first so GLB floor highlight still works
   * even when Three area entities are not available for every floor id.
   */

  /**
 * Process organizations and buildings, initializing 3D visualization for each building
 * Filters buildings by type and associates floors, then creates 3D models
 * @param orgs Organization array with perimeters
 * @param foreverEntitiesAdded Map to track added entities
 * @param callback Callback function when 3D view is ready for a building
 */
  public processBuildingsFor3DVisualization(
    orgs: any[], buildingId: any,
    foreverEntitiesAdded: any,
    callback?: (buildingEntity: any, geojsonArray: any[]) => void
  ): void {

    orgs.forEach((org: any) => {
      if (!org.perimeters || !Array.isArray(org.perimeters)) {
        return;
      }

      // Filter BUILDING type perimeters
      let buildingPerimeters = org.perimeters.filter(
        (perimeter: any) => perimeter.type === 'BUILDING'
      );

      // If buildingId is provided, filter to only that building
      if (buildingId) {
        buildingPerimeters = buildingPerimeters.filter(
          (perimeter: any) => perimeter.id === buildingId
        );
      }

      buildingPerimeters.forEach((buildingPerimeter: any) => {
        try {
          const floors = org.perimeters.filter(
            (f: any) =>
              f.type === 'FLOOR' &&
              f.ancestors &&
              f.ancestors.includes(buildingPerimeter.id)
          );

          const rooms = org.perimeters.filter(
            (r: any) =>
              r.type === 'ROOM' &&
              r.ancestors &&
              r.ancestors.some((ancestorId: string) =>
                floors.some((floor: any) => floor.id === ancestorId)
              )
          );

          if (!(buildingPerimeter.id in foreverEntitiesAdded.facilities)) {
            return;
          }

          const geojsonArray: any[] = [];

          floors.forEach((floor: any) => {
            geojsonArray.push({
              id: floor.id,
              label: floor.label,
              type: 'FLOOR',
              geometry: floor.geometry,
              ancestors: floor.ancestors || [],
              descendants: floor.descendants || []
            });
          });

          rooms.forEach((room: any) => {
            geojsonArray.push({
              id: room.id,
              label: room.label,
              type: 'ROOM',
              geometry: room.geometry,
              ancestors: room.ancestors || [],
              descendants: room.descendants || []
            });
          });

          const buildingEntity = foreverEntitiesAdded.facilities[buildingPerimeter.id];

          if (buildingEntity && geojsonArray.length > 0) {
            buildingEntity.perimeters = geojsonArray;
            buildingEntity.floorPerimeters = floors;
            buildingEntity.roomPerimeters = rooms;

            if (callback) {
              callback(buildingEntity, geojsonArray);
            }

          }
        } catch (error) {
          console.error(
            `Error processing building ${buildingPerimeter.label} for 3D:`,
            error
          );
        }
      });
    });
  }

  /**
  * Show/hide specific floor
  */
  toggleFloorVisibility(floorNumber: number, visible: boolean): void {
    if (!this.threeScene) return;

    this.threeScene.traverse((object) => {
      if (object.userData['floorNumber'] === floorNumber) {
        object.visible = visible;
      }
    });
  }

  /**
     * Highlight building
     */
  highlightBuilding(buildingId: string, highlight: boolean): void {
    const building = this.buildingModels.get(buildingId);
    if (!building) return;

    building.traverse((child: any) => {
      if (child instanceof THREE.Mesh) {
        if (highlight) {
          (child.material as THREE.MeshLambertMaterial).emissive = new THREE.Color(0x444444);
        } else {
          (child.material as THREE.MeshLambertMaterial).emissive = new THREE.Color(0x000000);
        }
      }
    });
  }

  /**
 * Update building model
 */
  updateBuildingModel(buildingId: string, updates: any): void {
    const model = this.buildingModels.get(buildingId);
    if (!model) return;

    if (updates.position) {
      const position = this.cesiumToThreePosition(
        updates.position.longitude,
        updates.position.latitude,
        updates.position.height
      );
      model.position.copy(position);
    }

    if (updates.scale) {
      model.scale.setScalar(updates.scale);
    }

    if (updates.rotation) {
      model.rotation.set(updates.rotation.x, updates.rotation.y, updates.rotation.z);
    }
  }

  /**
     * Convert Cesium coordinates to Three.js position using proper transformation matrix
     */
  private cesiumToThreePosition(longitude: number, latitude: number, height: number): THREE.Vector3 {
    if (!this.currentBuildingPosition) {
      return new THREE.Vector3(0, 0, 0);
    }

    // Calculate relative position from building reference point
    const deltaLon = longitude - this.currentBuildingPosition.longitude;
    const deltaLat = latitude - this.currentBuildingPosition.latitude;
    const deltaHeight = height - this.currentBuildingPosition.height;

    // Convert to meters using approximate conversion
    const earthRadius = 6378137.0; // Earth radius in meters
    const latInRadians = (this.currentBuildingPosition.latitude * Math.PI) / 180;

    const x = deltaLon * Math.PI / 180 * earthRadius * Math.cos(latInRadians);
    const y = deltaHeight;
    const z = -deltaLat * Math.PI / 180 * earthRadius;

    return new THREE.Vector3(x, y, z);
  }
  private showBuildingPopup(
    object: THREE.Object3D,
    data: any,
    x: number,
    y: number,
    buildingTopHeight: number
  ): void {
    this.selectedObject = object;

    this.zone.run(() => {
      this.buildingStateService.setSelectedBuilding(data);
      this.buildingStateService.setPopupPosition({ x, y });
    });
  }

  private createBuildingMarkerTexture(
    iconImg: HTMLImageElement
  ): THREE.Texture {

    const size = 100;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, size, size);

    /* ---------- BACKGROUND BOX ---------- */
    const radius = 18;
    ctx.fillStyle = '#000000ff';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 10;

    this.drawRoundedRect(ctx, 8, 8, size - 16, size - 16, radius);
    ctx.fill();
    ctx.shadowBlur = 0;

    const iconSize = 52;
    ctx.drawImage(
      iconImg,
      (size - iconSize) / 2,
      (size - iconSize) / 2 + 4,
      iconSize,
      iconSize
    );

    ctx.fillStyle = '#FFFFFF';
    ctx.font = '16px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('✕', size - 26, 26);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  }

  private drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
  ) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }
  private buildingIconImg!: HTMLImageElement;

  private loadBuildingIcon(): Promise<void> {
    if (this.buildingIconImg) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        this.buildingIconImg = img;
        resolve();
      };
      img.onerror = () => resolve();
      img.src = 'assets/img/icons/buildings.png';
    });
  }

  private addBuildingMarkerCard(
    buildingGroup: THREE.Group,
    buildingTopHeight: number,
    buildingId?: string | number
  ): void {
    const texture = this.createBuildingMarkerTexture(this.buildingIconImg);
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      depthWrite: false
    });

    const sprite = new THREE.Sprite(material);

    sprite.scale.set(8, 8, 1);

    sprite.position.set(
      0,
      buildingTopHeight + 8,
      0
    );

    sprite.renderOrder = 1000;

    sprite.userData = {
      type: 'building-marker',
      buildingId,
      height: buildingTopHeight
    };

    buildingGroup.userData['markerSprite'] = sprite;

    buildingGroup.add(sprite);
  }


  get wireframeReady$() {
    return this.blenderBuilding.wireframeReady$;
  }

  get floorHighlight$() {
    return this.blenderBuilding.floorHighlight$;
  }

  get alertFloorHighlight$() {
    return this.blenderBuilding.alertFloorHighlight$;
  }

  get roomHighlight$() {
    return this.blenderBuilding.roomHighlight$;
  }

  registerBabylonViewer(component: unknown): void {
    this.blenderBuilding.registerBabylonViewer(component);
  }

  onBabylonFloorClick(floorId: string, mesh: unknown, floorNumberHint?: number): void {
    this.blenderBuilding.onBabylonFloorClick(floorId, mesh, floorNumberHint);
  }

  getBuildingGeographicCenter(): { lat: number; lon: number } | null {
    return this.blenderBuilding.getBuildingGeographicCenter();
  }

  setOverlayVisible(visible: boolean): void {
    this.blenderBuilding.setOverlayVisible(visible);
  }

  prepareGlbMapOverlayContext(
    anchor?: { lat: number; lon: number },
    options?: { forceAnchor?: boolean }
  ): boolean {
    return this.blenderBuilding.prepareGlbMapOverlayContext(anchor, options);
  }

  setGlbMapGeographicCenter(anchor: { lat: number; lon: number }): void {
    this.blenderBuilding.setGlbMapGeographicCenter(anchor);
  }

  hideWireframeForGlbOverlay(): void {
    this.blenderBuilding.hideWireframeForGlbOverlay();
  }

  async showGlbMapOverlay(glbUrl: string, building: any): Promise<boolean> {
    const result = await this.blenderBuilding.showGlbMapOverlay(glbUrl, building);
    if (result) {
      this.setLightingMode(true);
    }
    return result;
  }

  async showCampusGlbMapOverlay(entries: Array<{ glbUrl: string; building: any }>): Promise<boolean> {
    const result = await this.blenderBuilding.showCampusGlbMapOverlay(entries);
    if (result) {
      this.setLightingMode(true);
    }
    return result;
  }

  isGlbMapOverlayActive(): boolean {
    return this.blenderBuilding.isGlbMapOverlayActive();
  }

  hideGlbMapOverlay(restoreWireframe = true): void {
    this.blenderBuilding.hideGlbMapOverlay(restoreWireframe);
    this.setLightingMode(false);
  }

  renderGlbMapOverlayFrame(): void {
    this.blenderBuilding.renderGlbMapOverlayFrame();
  }

  highlightAlertFloors(floorIds: string[]): void {
    this.blenderBuilding.highlightAlertFloors(floorIds);
  }

  highlightAlertRooms(roomIds: string[], buildingIds?: string[]): void {
    this.blenderBuilding.highlightAlertRooms(roomIds, buildingIds);
  }

  clearAlertFloorHighlights(): void {
    this.blenderBuilding.clearAlertFloorHighlights();
  }

  hasCampusGlbBuilding(building: any): boolean {
    return this.blenderBuilding.hasCampusGlbBuilding(building);
  }

  isCampusBuildingGlbActive(building: any): boolean {
    return this.blenderBuilding.isCampusBuildingGlbActive(building);
  }

  toggleCampusBuildingGlb(building: any): boolean {
    return this.blenderBuilding.toggleCampusBuildingGlb(building);
  }

  private buildBlenderHost(): ThreeBuildingHost {
    return {
      getThreeScene: () => this.threeScene,
      getThreeCamera: () => this.threeCamera,
      getThreeRenderer: () => this.threeRenderer,
      getControls: () => this.controls,
      getCesiumThreeContainer: () => this.cesiumThreeContainer,
      getBuildingGeographicCenter: () =>
        this.buildingGeographicCenter ? { ...this.buildingGeographicCenter } : null,
      setBuildingGeographicCenter: (center) => {
        this.buildingGeographicCenter = center;
      },
      getBuildingModels: () => this.buildingModels,
      getBuildingEntities: () => this.buildingEntities,
      getBuildingContainerGroup: () => this.buildingContainerGroup,
      getAreaMapEntities: () => this.areaMapEntities,
      isThreeJSActive: () => this.isThreeJSActive,
      setThreeJSActive: (active) => {
        this.isThreeJSActive = active;
      },
      getMouse: () => this.mouse,
      getRaycaster: () => this.raycaster,
      reinitializeThreeJSIntegration: (container) => {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
        this.initializeThreeJSIntegration(container);
      },
      startRenderLoop: () => this.startRenderLoop(),
      synchronizeCameras: () => this.synchronizeCameras(),
      resizeThreeJSRenderer: () => this.resizeThreeJSRenderer(),
      getPreviouslyHighlightedFloor: () => this.previouslyHighlightedFloor,
      setPreviouslyHighlightedFloor: (floorId) => {
        this.previouslyHighlightedFloor = floorId;
      },
      emitFloorClick: (floorId, floorNumberHint) => {
        this.floorClickSubject.next({ floorId, floorNumberHint });
      },
      focusFloorOnArea: (areaId, container) => this.focusFloorOnArea(areaId, container),
      highlightFloorById: (floorId, highlight, syncBabylon) =>
        this.highlightFloorById(floorId, highlight, syncBabylon),
    };
  }

}
